import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Shield, Users, Truck, MessageCircle, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

export default function AboutPage() {
  const { company, contact, insurance, reviews, assets } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About Thrifty Move | Family-Owned Melbourne Removalists | From $130/hr</title>
        <meta name="description" content="Thrifty Move is a family-owned Melbourne removal company from $130/hr. Clear pricing, reliable communication, careful handling. Fully insured with $10M public liability." />
        <link rel="canonical" href="https://thrifty-move.com.au/about" />
        <meta property="og:title" content="About Thrifty Move | Melbourne Removalists | From $130/hr" />
        <meta property="og:description" content="Family-owned Melbourne removal company from $130/hr. Clear pricing, careful handling, fully insured with $10M coverage." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "MovingCompany",
              "name": "Thrifty Move",
              "legalName": "DV Amur Services Pty Ltd",
              "foundingDate": "2024",
              "description": "Family-owned Melbourne removal company focused on clear pricing, reliable communication and careful handling.",
              "telephone": "1800 233 023",
              "email": "info@thrifty-move.com.au"
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              About Thrifty Move
            </h1>
            <p className="text-xl text-gray-300">
              Family-owned Melbourne removalists focused on clear pricing and reliable service
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="prose prose-gray">
                  <p className="text-gray-600 mb-4">
                    Thrifty Move started in Melbourne in {company.foundedDescription} with a straightforward idea: moving should be easier to understand, easier to organise and priced clearly.
                  </p>
                  <p className="text-gray-600 mb-4">
                    As a family-owned business, we focus on reliable communication, careful handling and matching each move with the right truck capacity and crew requirements.
                  </p>
                  <p className="text-gray-600">
                    Rather than asking customers to guess how large their move is, our online quote system allows customers to build their inventory, estimate the volume of their move and receive pricing based on their actual requirements.
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src={assets.truckPhoto}
                  alt="Thrifty Move truck ready for a Melbourne removal"
                  className="rounded-xl shadow-lg w-full"
                />
              </div>
            </div>

            {/* What We Do */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What We Do</h2>
              <p className="text-gray-600 text-center max-w-2xl mx-auto mb-8">
                We handle moves across Melbourne, from apartments and smaller furniture moves through to larger homes and office relocations, as well as packing and unpacking services.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['House Removals', 'Apartment Moves', 'Office Relocations', 'Furniture Delivery', 'Packing Services', 'Unpacking Help'].map((service, i) => (
                  <Card key={i} className="border border-gray-200">
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                      <span className="text-gray-900 font-medium text-sm">{service}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Our Approach */}
            <div className="bg-gray-50 rounded-2xl p-8 mb-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Approach</h2>
              <p className="text-gray-700 text-center text-lg max-w-2xl mx-auto">
                Our goal is simple: clear pricing, professional moving and a straightforward experience from the first quote request through to moving day.
              </p>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <Card className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <MessageCircle className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Communication</h3>
                  <p className="text-gray-600">
                    From your first enquiry to moving day, we keep you informed every step of the way. No guessing, no surprises.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <Truck className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Right Capacity</h3>
                  <p className="text-gray-600">
                    Our quote system helps match your move with the appropriate truck capacity and crew, so you get exactly what you need.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fully Insured</h3>
                  <p className="text-gray-600">
                    {insurance.description}. Your belongings are protected throughout the entire move.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Family Business</h3>
                  <p className="text-gray-600">
                    As a family-owned business, we treat every move with the same care we'd want for our own belongings.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Reviews Banner */}
            <Card className="border-2 border-yellow-400 bg-yellow-50 mb-16">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-2">{reviews.displayText}</p>
                <p className="text-gray-600 mb-4">See what our customers say about their moving experience</p>
                <Link to="/reviews">
                  <Button variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold">
                    Read Customer Reviews
                  </Button>
                </Link>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Move?
          </h2>
          <p className="text-gray-800 mb-8 max-w-xl mx-auto">
            Get your free quote and experience the Thrifty Move difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-bold px-10 py-5 text-lg">
                <ArrowRight className="mr-2 w-5 h-5" />
                Get a Free Quote
              </Button>
            </Link>
            <a href={contact.phoneLink} onClick={() => trackPhoneClick('About - Call')}>
              <Button variant="outline" className="w-full sm:w-auto border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold px-10 py-5 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                {contact.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
